/**
 * Helpers for showing a local time from its ISO 8601 string.
 *
 * Participant-facing API resources send race times with the UTC offset of the race's
 * timezone (e.g. 2026-07-01T09:00:00+02:00). Shifting the instant by that offset and
 * formatting as UTC renders the stored wall-clock, independent of the viewer's browser
 * time zone and of its time zone database.
 */

/** Offset in minutes of a trailing +HH:MM / -HH:MM (or Z) in an ISO 8601 string, or null. */
function isoOffsetMinutes(dateStr: string): number | null {
  if (/Z$/.test(dateStr)) return 0
  const match = /([+-])(\d{2}):?(\d{2})$/.exec(dateStr)
  if (!match) return null
  const sign = match[1] === '-' ? -1 : 1
  return sign * (Number(match[2]) * 60 + Number(match[3]))
}

function formatWithOffset(
  dateStr: string | null,
  options: Intl.DateTimeFormatOptions,
  locale?: string,
): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  const offsetMinutes = isoOffsetMinutes(dateStr)
  if (offsetMinutes === null) {
    return date.toLocaleString(locale, options)
  }
  const shifted = new Date(date.getTime() + offsetMinutes * 60_000)
  return shifted.toLocaleString(locale, { ...options, timeZone: 'UTC' })
}

export function formatLocalDateTime(dateStr: string | null, locale?: string): string {
  return formatWithOffset(dateStr, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }, locale)
}

export function formatLocalDate(dateStr: string | null, locale?: string): string {
  return formatWithOffset(dateStr, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }, locale)
}

/**
 * Option label for a time zone select: "<IANA id> (<offset today>)", e.g.
 * "Europe/Brussels (GMT+2)". Falls back to the bare id if the runtime cannot
 * resolve the zone.
 */
export function timezoneOptionLabel(timezone: string, now: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    }).formatToParts(now)
    const offset = parts.find((part) => part.type === 'timeZoneName')?.value
    return offset ? `${timezone} (${offset})` : timezone
  } catch {
    return timezone
  }
}
